const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const motionRoot = document.documentElement;
let enabled = motionRoot.hasAttribute('data-entrance-state');
const seen = new WeakSet();
const active = new Map();
const pending = new Set();
const waitingForScroll = new Set();
const mobileEntrance = matchMedia('(max-width: 59.999rem)');
const headingEnds = new WeakMap();
const headingStarts = new WeakMap();
const ease = 'cubic-bezier(.22,.75,.25,1)';
const softEase = 'cubic-bezier(.25,.55,.3,1)';
const settleEase = 'cubic-bezier(.22,.61,.36,1)';
const maximumDelay = 300;
const headingDuration = 750;
const segmenter = 'Segmenter' in Intl ? new Intl.Segmenter(document.documentElement.lang, { granularity: 'grapheme' }) : null;

function settle(element) {
  active.get(element)?.finish();
}

function animate(element, frames, options = {}) {
  if (!element || !enabled || reducedMotion.matches || element.contains(document.activeElement)) return;
  settle(element);
  // Choreography must not keep visible content waiting for another entrance to finish.
  const animation = element.animate(frames, {
    duration: 750, easing: softEase, fill: 'backwards', ...options,
    delay: Math.min(options.delay || 0, maximumDelay),
  });
  const finish = () => { animation.cancel(); active.delete(element); };
  active.set(element, { finish });
  animation.finished.then(finish, () => {});
}

function consume(element) {
  observer?.unobserve(element);
  pending.delete(element);
  waitingForScroll.delete(element);
  if (!waitingForScroll.size) window.removeEventListener('scroll', releaseScrollEntrances);
  seen.add(element);
  element.setAttribute('data-entrance-ready', '');
}

function revealHeading(heading, words = false, maximum = 1200) {
  if (!enabled || !segmenter || heading.querySelector('a, button, input') || heading.closest('[inert]')) return 0;
  const original = [...heading.childNodes];
  const label = heading.getAttribute('aria-label');
  const text = heading.textContent;
  const before = heading.getBoundingClientRect();
  const holder = document.createElement('span');
  holder.setAttribute('aria-hidden', 'true');
  holder.append(...original.map(node => node.cloneNode(true)));
  const walker = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  const characters = [];
  const splitter = words ? new Intl.Segmenter(document.documentElement.lang, { granularity: 'word' }) : segmenter;
  nodes.forEach(node => {
    const fragment = document.createDocumentFragment();
    for (const { segment } of splitter.segment(node.textContent)) {
      if (/^\s+$/u.test(segment)) fragment.append(document.createTextNode(segment));
      else {
        const character = document.createElement('span');
        character.textContent = segment;
        fragment.append(character);
        characters.push(character);
      }
    }
    node.replaceWith(fragment);
  });
  if (!characters.length) return 0;
  if (!words && characters.length > 40) return revealHeading(heading, true, maximum);
  const restore = () => {
    heading.replaceChildren(...original);
    if (label === null) heading.removeAttribute('aria-label');
    else heading.setAttribute('aria-label', label);
  };
  heading.setAttribute('aria-label', label || text);
  heading.replaceChildren(holder);
  const after = heading.getBoundingClientRect();
  // Font shaping and rich content take precedence over character choreography.
  if (Math.abs(before.height - after.height) > 0.5 || heading.scrollWidth > heading.clientWidth + 1) {
    restore();
    if (!words) return revealHeading(heading, true, maximum);
    animate(heading, [{ opacity: 0 }, { opacity: 1 }], { duration: 900 });
    return 900;
  }
  const total = Math.min(maximum, headingDuration + Math.min(maximumDelay, (characters.length - 1) * (words ? 95 : 35)));
  const stagger = (total - headingDuration) / Math.max(1, characters.length - 1);
  const animations = characters.map((character, index) => character.animate(
    [{ opacity: 0 }, { opacity: 1 }],
    { duration: headingDuration, delay: index * stagger, fill: 'backwards', easing: softEase },
  ));
  const finish = () => { animations.forEach(animation => animation.cancel()); restore(); active.delete(heading); };
  active.set(heading, { finish });
  Promise.all(animations.map(animation => animation.finished)).then(finish, () => {});
  return total;
}

function headingEntrance(heading, maximum = 1200) {
  if (!heading) return 0;
  if (!seen.has(heading)) {
    consume(heading);
    headingStarts.set(heading, performance.now());
    const duration = reducedMotion.matches ? 0 : revealHeading(heading, false, maximum);
    headingEnds.set(heading, performance.now() + duration);
  }
  return Math.max(0, (headingEnds.get(heading) || 0) - performance.now());
}

function revealHero(hero) {
  if (hero.dataset.motion === 'false') {
    hero.querySelectorAll('[data-entrance-heading]').forEach(consume);
    return;
  }
  const slide = hero.querySelector('[data-campaign-slide][data-active="true"]');
  if (!slide) return;
  headingEntrance(slide.querySelector('[data-entrance-heading]'), 1050);
  slide.querySelectorAll('.campaign-slide__eyebrow, .campaign-slide__description').forEach((node, index) => {
    animate(node, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 800, delay: index * 140 });
  });
  // Conceal only the foreground; image requests and the painted background remain immediate.
  slide.querySelectorAll('.campaign-artwork--subject .campaign-artwork__image').forEach(node => {
    animate(node, [{ opacity: 0, transform: 'translateY(24px) scale(.98)' }, { opacity: 1, transform: 'none' }], { duration: 1150, delay: 300, easing: ease });
  });
  slide.querySelectorAll('.campaign-slide__image, .campaign-artwork--full_canvas .campaign-artwork__image').forEach(node => {
    animate(node, [{ transform: 'scale(1.025)' }, { transform: 'none' }], { duration: 1050, delay: 300 });
  });
}

function revealRail(rail) {
  const mobile = matchMedia('(max-width: 47.999rem)').matches;
  const heading = rail.closest('collection-showcase').querySelector('[data-entrance-heading]');
  headingEntrance(heading, 1050);
  const bounds = rail.getBoundingClientRect();
  const visible = [...rail.querySelectorAll('.collection-discovery__entrance')]
    .map(node => ({ node, rect: node.getBoundingClientRect() }))
    .filter(({ rect }) => rect.right > bounds.left && rect.left < bounds.right)
    .sort((a, b) => a.rect.left - b.rect.left);
  visible.forEach(({ node }, index) => {
    const progression = Math.min(index, 3);
    const travel = (mobile ? 24 : 32) - progression * 1.25;
    animate(node, [
      { opacity: 0, transform: `translateX(${travel}px)` },
      { opacity: 1, offset: 0.55 },
      { opacity: 1, transform: 'none' },
    ], { duration: (mobile ? 600 : 680) + progression * 20, delay: index * (mobile ? 90 : 110), easing: settleEase });
  });
}

function revealMirror(stage) {
  const mobile = matchMedia('(max-width: 63.999rem)').matches;
  const story = stage.closest('mirror-story');
  headingEntrance(story.querySelector('[data-entrance-heading]'), 1000);
  story.querySelector('.mirror-story__chapter')?.querySelectorAll('[data-entrance-heading], [data-entrance]').forEach(node => {
    if (seen.has(node)) return;
    if (node.hasAttribute('data-entrance-heading')) headingEntrance(node);
    else { consume(node); animate(node, [{ opacity: 0 }, { opacity: 1 }], { duration: 780, delay: 100 }); }
  });
  // Move the shared canvas as one unit; scene transforms and sticky geometry stay owned by Mirror Story.
  animate(stage, [{ transform: `translateY(${mobile ? 14 : 28}px) scale(${mobile ? '.978' : '.96'})` }, { transform: 'none' }], { duration: mobile ? 900 : 1150, delay: mobile ? 120 : 160, easing: settleEase });
  animate(stage.querySelector('.mirror-story__frame'), [{ opacity: 0 }, { opacity: 1 }], { duration: mobile ? 650 : 800, delay: mobile ? 120 : 160, easing: settleEase });
  animate(stage.querySelector('.mirror-story__window'), [{ opacity: 0 }, { opacity: 1 }], { duration: mobile ? 700 : 850, delay: mobile ? 180 : 220, easing: settleEase });
  animate(stage.querySelector('.mirror-story__arrival-light'), [
    { opacity: 0, transform: 'translateX(-45%)' },
    { opacity: mobile ? 0.16 : 0.32, offset: 0.4 },
    { opacity: 0, transform: 'translateX(45%)' },
  ], { duration: mobile ? 580 : 720, delay: mobile ? 260 : 300, easing: settleEase });
}

function revealEditorial(element) {
  const mobile = matchMedia('(max-width: 63.999rem)').matches;
  const heading = element.closest('.editorial-feature').querySelector('[data-entrance-heading]');
  headingEntrance(heading, 1000);
  const media = element.matches('.editorial-feature__media');
  const offset = media ? (mobile ? 240 : 300) : 140;
  const delay = heading ? Math.max(0, (headingStarts.get(heading) || 0) + offset - performance.now()) : 0;
  if (media) {
    animate(element, [
      { opacity: 0, clipPath: `inset(0 0 ${mobile ? 4 : 8}% 0)`, transform: `scale(${mobile ? '.992' : '.98'})` },
      { opacity: 1, offset: 0.55 },
      { opacity: 1, clipPath: 'inset(0)', transform: 'none' },
    ], { duration: mobile ? 900 : 1050, delay, easing: settleEase });
  } else {
    animate(element, [{ opacity: 0 }, { opacity: 1 }], { duration: mobile ? 680 : 750, delay, easing: softEase });
  }
}

function reveal(element, index = 0) {
  if (!enabled || reducedMotion.matches || element.contains(document.activeElement)) {
    element.querySelectorAll('[data-entrance-heading]').forEach(consume);
    return;
  }
  if (element.matches('campaign-hero')) revealHero(element);
  else if (element.matches('[data-rail]')) revealRail(element);
  else if (element.hasAttribute('data-entrance-mirror')) revealMirror(element);
  else if (element.matches('.editorial-feature__media, .editorial-feature__text')) revealEditorial(element);
  else if (element.hasAttribute('data-entrance-heading')) {
    headingStarts.set(element, performance.now());
    const duration = revealHeading(element, false, element.closest('collection-showcase') ? 1050 : 1200);
    headingEnds.set(element, performance.now() + duration);
  } else if (element.hasAttribute('data-entrance-card')) {
    animate(element, [{ opacity: 0, transform: 'translateY(14px) scale(.99)' }, { opacity: 1, transform: 'none' }], { duration: 780, delay: Math.min(index, 3) * 100, easing: ease });
  } else if (element.hasAttribute('data-entrance-media') && element.closest('product-detail')) {
    animate(element, [{ transform: 'scale(.985)' }, { transform: 'none' }], { duration: 900 });
  } else {
    const heading = element.closest('.shopify-section')?.querySelector('[data-entrance-heading]');
    if (heading && !seen.has(heading)) {
      const bounds = heading.getBoundingClientRect();
      if (bounds.bottom > 0 && bounds.top < innerHeight) headingEntrance(heading);
    }
    const remaining = Math.max(0, (headingEnds.get(heading) || 0) - performance.now());
    if (element.matches('[data-entrance-media]')) {
      animate(element, [{ opacity: 0, clipPath: 'inset(0 0 12% 0)', transform: 'scale(.985)' }, { opacity: 1, clipPath: 'inset(0)', transform: 'none' }], { duration: 1100, delay: Math.max(120, remaining - 300), easing: ease });
    } else if (element.matches('details, .product-disclosures')) {
      animate(element, [{ opacity: 0 }, { opacity: 1 }], { duration: 700, delay: 100 });
    } else if (element.matches('.product-testimonials__quote')) {
      animate(element, [{ opacity: 0, transform: 'translateX(16px)' }, { opacity: 1, transform: 'none' }], { duration: 880, delay: Math.max(0, remaining - 400) });
    } else {
      animate(element, [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 820, delay: Math.max(0, remaining - 450) });
    }
  }
}

const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
  const visible = entries.filter(({ target, isIntersecting, intersectionRatio, boundingClientRect, rootBounds }) =>
    isIntersecting && !target.closest('[inert]') &&
    (!target.matches('.collection-discovery__intro [data-entrance-heading]') || intersectionRatio === 1 || boundingClientRect.height > (rootBounds?.height || innerHeight)))
    .map(({ target }) => ({ target, rect: target.getBoundingClientRect() }));
  visible.sort((a, b) => Math.abs(a.rect.top - b.rect.top) > 8 ? a.rect.top - b.rect.top : a.rect.left - b.rect.left);
  let cardIndex = 0;
  visible.forEach(({ target }) => {
    if (seen.has(target)) return;
    consume(target);
    try {
      reveal(target, target.hasAttribute('data-entrance-card') ? cardIndex++ : 0);
    } catch {
      enabled = false;
      motionRoot.removeAttribute('data-entrance-state');
      finishWithin(document);
    }
  });
}, { threshold: [0, 1] }) : null;

function releaseScrollEntrances() {
  if (window.scrollY <= 0 && mobileEntrance.matches) return;
  window.removeEventListener('scroll', releaseScrollEntrances);
  waitingForScroll.forEach(element => observer.observe(element));
  waitingForScroll.clear();
}

function connect(root) {
  if (!observer || !enabled) return;
  root.querySelectorAll('campaign-hero, [data-entrance-heading], [data-entrance], [data-entrance-card], [data-entrance-media], [data-entrance-mirror], collection-showcase [data-rail]').forEach(element => {
    if (seen.has(element) || pending.has(element) || element.closest('quick-add-dialog')) return;
    if (element.closest('campaign-hero')?.dataset.motion === 'false') {
      consume(element);
      return;
    }
    if (element.matches('[data-entrance-heading]') && element.closest('campaign-hero')) {
      if (!element.closest('[data-campaign-slide][data-active="true"]')) consume(element);
      return;
    }
    pending.add(element);
    const followsHero = element.closest('.shopify-section')?.previousElementSibling?.querySelector('campaign-hero');
    if (mobileEntrance.matches && !reducedMotion.matches && window.scrollY <= 0 && !window.Shopify?.designMode && followsHero &&
        element.matches('.collection-discovery__intro [data-entrance-heading], collection-showcase [data-rail]')) {
      waitingForScroll.add(element);
      window.addEventListener('scroll', releaseScrollEntrances, { passive: true });
      return;
    }
    observer.observe(element);
  });
}
function finishWithin(root) {
  for (const element of active.keys()) if (root.contains(element)) settle(element);
  for (const element of pending) if (root.contains(element)) {
    consume(element);
    element.querySelectorAll('[data-entrance-heading]').forEach(consume);
  }
}

connect(document);
if (enabled) motionRoot.dataset.entranceState = 'ready';
document.dispatchEvent(new Event('entrance:ready'));
document.addEventListener('collection:before-render', event => finishWithin(event.target));
document.addEventListener('collection:render', event => connect(event.target));
document.addEventListener('shopify:section:load', event => connect(event.target));
document.addEventListener('shopify:section:unload', event => finishWithin(event.target));
document.addEventListener('shopify:block:select', event => finishWithin(event.target));
document.addEventListener('focusin', event => {
  for (const element of pending) if (element.contains(event.target)) {
    consume(element);
    element.querySelectorAll('[data-entrance-heading]').forEach(consume);
  }
  for (const element of active.keys()) if (element.contains(event.target)) settle(element);
});
window.addEventListener('pagehide', () => finishWithin(document));
mobileEntrance.addEventListener('change', releaseScrollEntrances);
reducedMotion.addEventListener('change', () => {
  if (reducedMotion.matches) {
    enabled = false;
    motionRoot.removeAttribute('data-entrance-state');
    finishWithin(document);
  }
});
